import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import Breadcrumb from '../../components/layout/Breadcrumb';
import Button from '../../components/ui/Button';
import EmptyState from '../../components/ui/EmptyState';
import ErrorState from '../../components/ui/ErrorState';
import Input from '../../components/ui/Input';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import Modal from '../../components/ui/Modal';
import Select from '../../components/ui/Select';
import ToastContainer from '../../components/ui/ToastContainer';
import { useToast } from '../../hooks/useToast';
import {
  createHospitalEmailDomain,
  createHospitalSite,
  deleteHospitalEmailDomain,
  deleteHospitalSite,
  fetchHospitalSites,
  updateHospitalEmailDomain,
  updateHospitalSite,
} from '../../services/hospitalEmailDomainApi';
import {
  CreateHospitalEmailDomainInput,
  HOSPITAL_DOMAIN_CLASSIFICATION_LABELS,
  HOSPITAL_GRANTED_ACCESS_LABELS,
  HospitalDomainClassification,
  HospitalEmailDomain,
  HospitalGrantedAccessLevel,
  HospitalSite,
  defaultGrantedAccessLevel,
} from '../../types/hospitalEmailDomain';

const CLASSIFICATION_OPTIONS = [
  { value: '', label: 'All types' },
  ...Object.values(HospitalDomainClassification).map((value) => ({
    value,
    label: HOSPITAL_DOMAIN_CLASSIFICATION_LABELS[value],
  })),
];

const ACCESS_FILTER_OPTIONS = [
  { value: '', label: 'All access grants' },
  { value: 'ONTARIO_HEALTHCARE_PROFESSIONALS', label: 'Ontario Healthcare Professional' },
  { value: 'PUBLIC', label: 'No extra access' },
];

const ACTIVE_FILTER_OPTIONS = [
  { value: '', label: 'Active and inactive' },
  { value: 'true', label: 'Active only' },
  { value: 'false', label: 'Inactive only' },
];

const CLASSIFICATION_FORM_OPTIONS = Object.values(HospitalDomainClassification).map((value) => ({
  value,
  label: HOSPITAL_DOMAIN_CLASSIFICATION_LABELS[value],
}));

const ACCESS_FORM_OPTIONS = (Object.keys(HOSPITAL_GRANTED_ACCESS_LABELS) as HospitalGrantedAccessLevel[]).map(
  (value) => ({
    value,
    label: HOSPITAL_GRANTED_ACCESS_LABELS[value],
  })
);

type DomainFormState = {
  domain: string;
  classification: HospitalDomainClassification;
  grantedAccessLevel: HospitalGrantedAccessLevel;
  organizationOrService: string;
  notes: string;
  active: boolean;
};

const emptyDomainForm = (): DomainFormState => ({
  domain: '',
  classification: HospitalDomainClassification.HOSPITAL,
  grantedAccessLevel: 'ONTARIO_HEALTHCARE_PROFESSIONALS',
  organizationOrService: '',
  notes: '',
  active: true,
});

export default function HospitalDomains() {
  const { toasts, success, error: showError, removeToast } = useToast();
  const [sites, setSites] = useState<HospitalSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [classificationFilter, setClassificationFilter] = useState('');
  const [accessFilter, setAccessFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [busyId, setBusyId] = useState<string | null>(null);

  const [siteModalOpen, setSiteModalOpen] = useState(false);
  const [editingSite, setEditingSite] = useState<HospitalSite | null>(null);
  const [siteName, setSiteName] = useState('');

  const [domainModalOpen, setDomainModalOpen] = useState(false);
  const [domainSite, setDomainSite] = useState<HospitalSite | null>(null);
  const [editingDomain, setEditingDomain] = useState<HospitalEmailDomain | null>(null);
  const [domainForm, setDomainForm] = useState<DomainFormState>(emptyDomainForm());

  const loadSites = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchHospitalSites({
        search: searchQuery,
        classification: classificationFilter as HospitalDomainClassification | '',
        grantedAccessLevel: accessFilter as HospitalGrantedAccessLevel | '',
        active: activeFilter as '' | 'true' | 'false',
      });
      setSites(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load hospital sites'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadSites();
    }, searchQuery ? 250 : 0);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when filters change
  }, [searchQuery, classificationFilter, accessFilter, activeFilter]);

  useEffect(() => {
    if (searchQuery.trim()) {
      setExpandedIds(new Set(sites.map((site) => site.id)));
    }
  }, [sites, searchQuery]);

  const stats = useMemo(() => {
    const domainCount = sites.reduce((sum, site) => sum + site.domains.length, 0);
    const ohpCount = sites.reduce(
      (sum, site) =>
        sum +
        site.domains.filter(
          (domain) => domain.active && domain.grantedAccessLevel === 'ONTARIO_HEALTHCARE_PROFESSIONALS'
        ).length,
      0
    );
    return { siteCount: sites.length, domainCount, ohpCount };
  }, [sites]);

  const toggleExpanded = (siteId: string) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  };

  const openCreateSite = () => {
    setEditingSite(null);
    setSiteName('');
    setSiteModalOpen(true);
  };

  const openEditSite = (site: HospitalSite) => {
    setEditingSite(site);
    setSiteName(site.name);
    setSiteModalOpen(true);
  };

  const openCreateDomain = (site: HospitalSite) => {
    setDomainSite(site);
    setEditingDomain(null);
    setDomainForm(emptyDomainForm());
    setDomainModalOpen(true);
  };

  const openEditDomain = (site: HospitalSite, domain: HospitalEmailDomain) => {
    setDomainSite(site);
    setEditingDomain(domain);
    setDomainForm({
      domain: domain.domain,
      classification: domain.classification,
      grantedAccessLevel: domain.grantedAccessLevel,
      organizationOrService: domain.organizationOrService || '',
      notes: domain.notes || '',
      active: domain.active,
    });
    setDomainModalOpen(true);
  };

  const handleSaveSite = async (event: FormEvent) => {
    event.preventDefault();
    const name = siteName.trim();
    if (!name) return;
    try {
      setBusyId(editingSite?.id || 'site');
      if (editingSite) {
        const updated = await updateHospitalSite(editingSite.id, name);
        setSites((current) => current.map((site) => (site.id === updated.id ? updated : site)));
        success('Site name updated');
      } else {
        const created = await createHospitalSite(name);
        setSites((current) => [...current, created].sort((a, b) => a.name.localeCompare(b.name)));
        setExpandedIds((current) => new Set(current).add(created.id));
        success('Site added');
      }
      setSiteModalOpen(false);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save site');
    } finally {
      setBusyId(null);
    }
  };

  const handleDeleteSite = async (site: HospitalSite) => {
    if (!confirm(`Delete "${site.name}" and all of its email domains? This cannot be undone.`)) return;
    try {
      setBusyId(site.id);
      await deleteHospitalSite(site.id);
      setSites((current) => current.filter((item) => item.id !== site.id));
      success('Site deleted');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete site');
    } finally {
      setBusyId(null);
    }
  };

  const handleSaveDomain = async (event: FormEvent) => {
    event.preventDefault();
    if (!domainSite) return;
    const payload: CreateHospitalEmailDomainInput = {
      domain: domainForm.domain.trim(),
      classification: domainForm.classification,
      grantedAccessLevel: domainForm.grantedAccessLevel,
      active: domainForm.active,
      organizationOrService: domainForm.organizationOrService.trim() || null,
      notes: domainForm.notes.trim() || null,
    };
    try {
      setBusyId(editingDomain?.id || domainSite.id);
      if (editingDomain) {
        const updated = await updateHospitalEmailDomain(editingDomain.id, payload);
        setSites((current) =>
          current.map((site) =>
            site.id === domainSite.id
              ? {
                  ...site,
                  domains: site.domains.map((domain) => (domain.id === updated.id ? updated : domain)),
                }
              : site
          )
        );
        success('Domain updated');
      } else {
        const created = await createHospitalEmailDomain(domainSite.id, payload);
        setSites((current) =>
          current.map((site) =>
            site.id === domainSite.id ? { ...site, domains: [...site.domains, created] } : site
          )
        );
        success('Domain added');
      }
      setDomainModalOpen(false);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save domain');
    } finally {
      setBusyId(null);
    }
  };

  const handleAccessChange = async (site: HospitalSite, domain: HospitalEmailDomain, value: string) => {
    try {
      setBusyId(domain.id);
      const updated = await updateHospitalEmailDomain(domain.id, {
        grantedAccessLevel: value as HospitalGrantedAccessLevel,
      });
      setSites((current) =>
        current.map((item) =>
          item.id === site.id
            ? { ...item, domains: item.domains.map((entry) => (entry.id === updated.id ? updated : entry)) }
            : item
        )
      );
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update access');
    } finally {
      setBusyId(null);
    }
  };

  const handleActiveChange = async (site: HospitalSite, domain: HospitalEmailDomain, active: boolean) => {
    try {
      setBusyId(domain.id);
      const updated = await updateHospitalEmailDomain(domain.id, { active });
      setSites((current) =>
        current.map((item) =>
          item.id === site.id
            ? { ...item, domains: item.domains.map((entry) => (entry.id === updated.id ? updated : entry)) }
            : item
        )
      );
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update domain');
    } finally {
      setBusyId(null);
    }
  };

  const handleDeleteDomain = async (site: HospitalSite, domain: HospitalEmailDomain) => {
    if (!confirm(`Remove @${domain.domain} from ${site.name}?`)) return;
    try {
      setBusyId(domain.id);
      await deleteHospitalEmailDomain(domain.id);
      setSites((current) =>
        current.map((item) =>
          item.id === site.id
            ? { ...item, domains: item.domains.filter((entry) => entry.id !== domain.id) }
            : item
        )
      );
      success('Domain removed');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete domain');
    } finally {
      setBusyId(null);
    }
  };

  if (loading && sites.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSkeleton lines={8} />
      </div>
    );
  }

  if (error && sites.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorState error={error} title="Failed to Load Hospital Domains" onRetry={loadSites} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <ToastContainer toasts={toasts} onClose={removeToast} />

      <Breadcrumb
        items={[
          { label: 'Home', to: '/' },
          { label: 'Hospital domains' },
        ]}
      />

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Hospital domains</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage hospital sites and email domains that grant Ontario Healthcare Professional access
            (including Data). Foundations default to no extra access; labs, research, and networks grant OHP
            unless you change them.
          </p>
          <p className="mt-2 text-sm text-gray-500">
            {stats.siteCount} sites · {stats.domainCount} domains · {stats.ohpCount} granting OHP
          </p>
        </div>
        <Button onClick={openCreateSite} className="flex items-center gap-2 min-h-[44px]">
          <PlusIcon className="h-5 w-5" />
          Add site
        </Button>
      </div>

      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="relative md:col-span-2 xl:col-span-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search sites or domains"
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:border-primary focus:ring-2 focus:ring-primary"
            aria-label="Search sites or domains"
          />
        </div>
        <Select
          options={CLASSIFICATION_OPTIONS}
          value={classificationFilter}
          onChange={(event) => setClassificationFilter(event.target.value)}
          aria-label="Filter by type"
        />
        <Select
          options={ACCESS_FILTER_OPTIONS}
          value={accessFilter}
          onChange={(event) => setAccessFilter(event.target.value)}
          aria-label="Filter by access grant"
        />
        <Select
          options={ACTIVE_FILTER_OPTIONS}
          value={activeFilter}
          onChange={(event) => setActiveFilter(event.target.value)}
          aria-label="Filter by active state"
        />
      </div>

      {sites.length === 0 ? (
        <EmptyState
          title="No hospital sites found"
          description="Try a different search, or add a site and its email domains."
          actionLabel="Add site"
          onAction={openCreateSite}
        />
      ) : (
        <div className="space-y-3">
          {sites.map((site) => {
            const expanded = expandedIds.has(site.id);
            return (
              <div
                key={site.id}
                className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4">
                  <button
                    type="button"
                    onClick={() => toggleExpanded(site.id)}
                    className="flex items-center gap-2 text-left min-h-[44px] flex-1 focus-ring rounded-md px-1"
                    aria-expanded={expanded}
                  >
                    {expanded ? (
                      <ChevronDownIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronRightIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                    )}
                    <span>
                      <span className="font-semibold text-gray-900">{site.name}</span>
                      <span className="block text-sm text-gray-500">
                        {site.domains.length} domain{site.domains.length === 1 ? '' : 's'}
                      </span>
                    </span>
                  </button>
                  <div className="flex flex-wrap gap-2 sm:justify-end">
                    <Button
                      variant="secondary"
                      className="min-h-[44px] px-4 py-2 text-sm"
                      onClick={() => openCreateDomain(site)}
                    >
                      Add domain
                    </Button>
                    <Button
                      variant="tertiary"
                      className="min-h-[44px] px-4 py-2 text-sm"
                      onClick={() => openEditSite(site)}
                    >
                      Rename
                    </Button>
                    <Button
                      variant="tertiary"
                      className="min-h-[44px] px-4 py-2 text-sm text-error"
                      onClick={() => handleDeleteSite(site)}
                      disabled={busyId === site.id}
                    >
                      Delete
                    </Button>
                  </div>
                </div>

                {expanded && (
                  <div className="border-t border-gray-200 overflow-x-auto">
                    {site.domains.length === 0 ? (
                      <p className="p-4 text-sm text-gray-600">No email domains on this site yet.</p>
                    ) : (
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-left text-gray-600">
                          <tr>
                            <th className="px-4 py-3 font-medium">Domain</th>
                            <th className="px-4 py-3 font-medium">Type</th>
                            <th className="px-4 py-3 font-medium">Access</th>
                            <th className="px-4 py-3 font-medium">Active</th>
                            <th className="px-4 py-3 font-medium sr-only">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {site.domains.map((domain) => (
                            <tr key={domain.id} className="border-t border-gray-100">
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-900">@{domain.domain}</div>
                                {domain.organizationOrService && (
                                  <div className="text-gray-500">{domain.organizationOrService}</div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-gray-700">
                                {HOSPITAL_DOMAIN_CLASSIFICATION_LABELS[domain.classification]}
                              </td>
                              <td className="px-4 py-3 min-w-[16rem]">
                                <Select
                                  options={ACCESS_FORM_OPTIONS}
                                  value={domain.grantedAccessLevel}
                                  disabled={busyId === domain.id}
                                  onChange={(event) => handleAccessChange(site, domain, event.target.value)}
                                  aria-label={`Access grant for ${domain.domain}`}
                                />
                              </td>
                              <td className="px-4 py-3">
                                <label className="inline-flex items-center gap-2 min-h-[44px]">
                                  <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    checked={domain.active}
                                    disabled={busyId === domain.id}
                                    onChange={(event) => handleActiveChange(site, domain, event.target.checked)}
                                  />
                                  <span className="text-gray-700">{domain.active ? 'Active' : 'Inactive'}</span>
                                </label>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    className="min-h-[44px] px-3 py-2 text-sm"
                                    onClick={() => openEditDomain(site, domain)}
                                  >
                                    Edit
                                  </Button>
                                  <button
                                    type="button"
                                    className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-gray-500 hover:text-error focus-ring rounded-md"
                                    aria-label={`Remove ${domain.domain}`}
                                    onClick={() => handleDeleteDomain(site, domain)}
                                  >
                                    <TrashIcon className="h-5 w-5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={siteModalOpen}
        onClose={() => setSiteModalOpen(false)}
        title={editingSite ? 'Rename site' : 'Add hospital site'}
      >
        <form onSubmit={handleSaveSite} className="space-y-4">
          <Input
            label="Site name"
            value={siteName}
            onChange={(event) => setSiteName(event.target.value)}
            required
          />
          <div className="flex justify-end gap-3">
            <Button type="button" variant="tertiary" onClick={() => setSiteModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!siteName.trim() || busyId !== null}>
              Save
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={domainModalOpen}
        onClose={() => setDomainModalOpen(false)}
        title={editingDomain ? `Edit @${editingDomain.domain}` : `Add domain${domainSite ? ` — ${domainSite.name}` : ''}`}
        size="lg"
      >
        <form onSubmit={handleSaveDomain} className="space-y-4">
          <Input
            label="Email domain"
            value={domainForm.domain}
            onChange={(event) => setDomainForm((current) => ({ ...current, domain: event.target.value }))}
            placeholder="hospital.on.ca"
            required
          />
          <Select
            label="Type"
            options={CLASSIFICATION_FORM_OPTIONS}
            value={domainForm.classification}
            onChange={(event) => {
              const classification = event.target.value as HospitalDomainClassification;
              setDomainForm((current) => ({
                ...current,
                classification,
                grantedAccessLevel: editingDomain
                  ? current.grantedAccessLevel
                  : defaultGrantedAccessLevel(classification),
              }));
            }}
          />
          <Select
            label="Access grant"
            options={ACCESS_FORM_OPTIONS}
            value={domainForm.grantedAccessLevel}
            onChange={(event) =>
              setDomainForm((current) => ({
                ...current,
                grantedAccessLevel: event.target.value as HospitalGrantedAccessLevel,
              }))
            }
          />
          <Input
            label="Organization or service (optional)"
            value={domainForm.organizationOrService}
            onChange={(event) =>
              setDomainForm((current) => ({ ...current, organizationOrService: event.target.value }))
            }
          />
          <Input
            label="Notes (optional)"
            value={domainForm.notes}
            onChange={(event) => setDomainForm((current) => ({ ...current, notes: event.target.value }))}
          />
          <label className="inline-flex items-center gap-2 min-h-[44px]">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              checked={domainForm.active}
              onChange={(event) => setDomainForm((current) => ({ ...current, active: event.target.checked }))}
            />
            <span className="text-sm text-gray-700">Active</span>
          </label>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="tertiary" onClick={() => setDomainModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!domainForm.domain.trim() || busyId !== null}>
              Save
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
